import React, { useMemo, useState } from 'react'
import type { MarketplaceJob } from '../../../services/backendService'
import '../styles/JobList.css'

interface JobListProps {
  jobs: MarketplaceJob[]
  onSelectJob: (job: MarketplaceJob) => void
}

export const JobList: React.FC<JobListProps> = ({ jobs, onSelectJob }) => {
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'in_progress' | 'completed'>('all')
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return '#2563eb'
      case 'in_progress':
        return '#7e22ce'
      case 'completed':
        return '#16a34a'
      case 'delivered':
        return '#16a34a'
      case 'cancelled':
        return '#f44336'
      default:
        return '#757575'
    }
  }

  const getOfferCount = (job: MarketplaceJob) => job.pricingModel === 'fixed_price' ? job.quoteCount : job.bidCount
  const getOfferLabel = (job: MarketplaceJob) => job.pricingModel === 'fixed_price' ? 'quote' : 'bid'
  const filteredJobs = useMemo(
    () => statusFilter === 'all' ? jobs : jobs.filter(job =>
      statusFilter === 'completed'
        ? job.status === 'completed' || job.status === 'delivered'
        : job.status === statusFilter
    ),
    [jobs, statusFilter]
  )

  return (
    <div className="job-list">
      {jobs.length > 0 && (
        <div className="job-list-controls">
          <div className="job-status-filters" aria-label="My jobs status filters">
            <button
              type="button"
              className={`job-status-filter ${statusFilter === 'all' ? 'is-active is-all' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({jobs.length})
            </button>
            <button
              type="button"
              className={`job-status-filter ${statusFilter === 'open' ? 'is-active is-open' : ''}`}
              onClick={() => setStatusFilter('open')}
            >
              Open ({jobs.filter(job => job.status === 'open').length})
            </button>
            <button
              type="button"
              className={`job-status-filter ${statusFilter === 'in_progress' ? 'is-active is-in-progress' : ''}`}
              onClick={() => setStatusFilter('in_progress')}
            >
              In Progress ({jobs.filter(job => job.status === 'in_progress').length})
            </button>
            <button
              type="button"
              className={`job-status-filter ${statusFilter === 'completed' ? 'is-active is-completed' : ''}`}
              onClick={() => setStatusFilter('completed')}
            >
              Completed ({jobs.filter(job => job.status === 'completed' || job.status === 'delivered').length})
            </button>
          </div>

          <div className="job-view-toggle" aria-label="My jobs view mode">
            <button
              type="button"
              className={`job-view-toggle-btn ${viewMode === 'card' ? 'is-active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`job-view-toggle-btn ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
        </div>
      )}

      {filteredJobs.length === 0 ? (
        <div className="empty-state">
          <p>{jobs.length === 0 ? 'No jobs yet. Create your first job to get started!' : 'No jobs match the selected filter.'}</p>
        </div>
      ) : (
        <div className={`jobs-grid ${viewMode !== 'card' ? 'jobs-list-view' : ''}`}>
          {filteredJobs.map(job => (
            <div
              key={job.id}
              className={`job-card ${viewMode !== 'card' ? 'job-card-list job-card-compact' : ''}`.trim()}
              onClick={() => onSelectJob(job)}
            >
              <div className="job-header">
                <h3>{job.title}</h3>
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(job.status) }}
                >
                  {job.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <p className="job-description">{job.description}</p>

              <div className="job-specs">
                <div className="spec">
                  <span className="label">Material:</span>
                  <span className="value">{job.material}</span>
                </div>
                <div className="spec">
                  <span className="label">Quality:</span>
                  <span className="value">{job.qualityLevel}</span>
                </div>
                <div className="spec">
                  <span className="label">Deadline:</span>
                  <span className="value">{job.deadline}</span>
                </div>
              </div>

              <div className="job-footer">
                <span className="pricing-model">
                  {job.pricingModel === 'bid' ? 'Bid System' : 'Fixed Price'}
                </span>
                {job.pricingModel === 'fixed_price' && job.fixedPrice && (
                  <span className="price">${job.fixedPrice.toFixed(2)}</span>
                )}
              </div>

              {typeof getOfferCount(job) === 'number' && (
                <div className="bid-count">
                  {getOfferCount(job)} {getOfferLabel(job)}{getOfferCount(job) === 1 ? '' : 's'}
                </div>
              )}

              <button className="btn-view-details">View Details →</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

